import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageCircle, X, Send, Bot, User, Loader2, Minimize2, Maximize2 } from 'lucide-react';
import openaiClient from '../utils/openaiClient';

const FloatingAIChat = ({ context = {} }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [isMinimized, setIsMinimized] = useState(false);
    const [messages, setMessages] = useState([]);
    const [inputMessage, setInputMessage] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [trainingStatus, setTrainingStatus] = useState('updated'); // 'updated', 'updating', 'outdated'
    const messagesEndRef = useRef(null);
    const inputRef = useRef(null);

    // Format message content with markdown-like rendering
    const formatMessage = (content) => {
        if (!content) return null;

        // Split by lines
        const lines = content.split('\n');
        const elements = [];
        let key = 0;

        for (let i = 0; i < lines.length; i++) {
            const line = lines[i];

            // Headers
            if (line.startsWith('# ')) {
                elements.push(<h1 key={key++} className="text-lg font-bold mt-3 mb-2">{line.substring(2)}</h1>);
            } else if (line.startsWith('## ')) {
                elements.push(<h2 key={key++} className="text-base font-bold mt-2 mb-1">{line.substring(3)}</h2>);
            } else if (line.startsWith('### ')) {
                elements.push(<h3 key={key++} className="text-sm font-semibold mt-2 mb-1">{line.substring(4)}</h3>);
            }
            // Horizontal rule
            else if (line.trim() === '---') {
                elements.push(<hr key={key++} className="my-2 border-gray-300" />);
            }
            // Bullet points
            else if (line.trim().startsWith('•') || line.trim().startsWith('-')) {
                const text = line.trim().substring(1).trim();
                elements.push(<div key={key++} className="ml-2 mb-1">• {formatInlineMarkdown(text)}</div>);
            }
            // Checkmarks and crosses
            else if (line.trim().startsWith('✓') || line.trim().startsWith('✗')) {
                const text = line.trim().substring(1).trim();
                elements.push(<div key={key++} className="ml-2 mb-1">{line.trim().charAt(0)} {formatInlineMarkdown(text)}</div>);
            }
            // Regular text
            else if (line.trim()) {
                elements.push(<p key={key++} className="mb-1">{formatInlineMarkdown(line)}</p>);
            }
            // Empty line
            else {
                elements.push(<div key={key++} className="h-2"></div>);
            }
        }

        return elements;
    };

    // Format inline markdown (bold, italic, etc.)
    const formatInlineMarkdown = (text) => {
        const parts = [];
        let current = '';
        let i = 0;
        let key = 0;

        while (i < text.length) {
            // Bold **text**
            if (text.substring(i, i + 2) === '**') {
                if (current) {
                    parts.push(<span key={key++}>{current}</span>);
                    current = '';
                }
                const end = text.indexOf('**', i + 2);
                if (end !== -1) {
                    parts.push(<strong key={key++}>{text.substring(i + 2, end)}</strong>);
                    i = end + 2;
                } else {
                    current += text[i];
                    i++;
                }
            }
            // Italic *text*
            else if (text[i] === '*' && text[i + 1] !== '*') {
                if (current) {
                    parts.push(<span key={key++}>{current}</span>);
                    current = '';
                }
                const end = text.indexOf('*', i + 1);
                if (end !== -1) {
                    parts.push(<em key={key++}>{text.substring(i + 1, end)}</em>);
                    i = end + 1;
                } else {
                    current += text[i];
                    i++;
                }
            }
            else {
                current += text[i];
                i++;
            }
        }

        if (current) {
            parts.push(<span key={key++}>{current}</span>);
        }

        return parts.length > 0 ? parts : text;
    };

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    useEffect(() => {
        scrollToBottom();
    }, [messages]);

    useEffect(() => {
        if (isOpen && !isMinimized && inputRef.current) {
            inputRef.current.focus();
        }
    }, [isOpen, isMinimized]);

    // Check training status on component mount and poll for updates
    useEffect(() => {
        let isMounted = true;
        let consecutiveFailures = 0;
        let intervalId = null;

        const checkTrainingStatus = async (retryCount = 0) => {
            try {
                // Create AbortController for timeout
                const controller = new AbortController();
                const timeoutId = setTimeout(() => controller.abort(), 5000); // 5 second timeout

                const response = await fetch('/api/ai/training-status', {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                    },
                    cache: 'no-cache', // Prevent caching issues
                    signal: controller.signal,
                });

                clearTimeout(timeoutId);

                if (response.ok) {
                    const data = await response.json();
                    if (isMounted) {
                        setTrainingStatus(data.status || 'updated');
                    }
                    consecutiveFailures = 0; // Reset failure count on success
                } else {
                    if (isMounted && consecutiveFailures === 0) {
                        // Only log once, not every time
                        setTrainingStatus('updated'); // Default to updated
                    }
                    consecutiveFailures++;
                    
                    // Stop polling if endpoint consistently fails
                    if (consecutiveFailures > 3 && intervalId) {
                        clearInterval(intervalId);
                        intervalId = null;
                    }
                }
            } catch (error) {
                consecutiveFailures++;
                
                // Only log errors on first failure, and stop polling after multiple failures
                if (consecutiveFailures === 1 && isMounted) {
                    // Silently fail - training status is not critical
                    setTrainingStatus('updated'); // Default to updated
                }

                // Stop polling if endpoint consistently fails
                if (consecutiveFailures > 3 && intervalId) {
                    clearInterval(intervalId);
                    intervalId = null;
                }
            }
        };

        // Initial check
        checkTrainingStatus();

        // Poll for training status updates every 60 seconds (only if endpoint is working)
        intervalId = setInterval(() => {
            if (consecutiveFailures <= 3) {
                checkTrainingStatus();
            }
        }, 60000);

        return () => {
            isMounted = false;
            if (intervalId) clearInterval(intervalId);
        };
    }, []);

    const handleSendMessage = async () => {
        if (!inputMessage.trim() || isLoading) return;

        const userMessage = {
            id: Date.now(),
            role: 'user',
            content: inputMessage.trim(),
            timestamp: new Date()
        };

        setMessages(prev => [...prev, userMessage]);
        setInputMessage('');
        setIsLoading(true);

        try {
            let response;

            // Use context-specific methods if available
            if (context.type === 'healthcare') {
                response = await openaiClient.askHealthcareQuestion(inputMessage, context);
            } else if (context.type === 'school') {
                response = await openaiClient.askSchoolQuestion(inputMessage, context.schoolData);
            } else {
                response = await openaiClient.chat(inputMessage);
            }

            // Normalize OpenAI-compatible responses and guard fallbacks
            const normalized = (resp) => {
                if (!resp) return '';
                if (Array.isArray(resp.choices) && resp.choices.length > 0) {
                    const c = resp.choices[0];
                    if (c && c.message && typeof c.message.content === 'string') return c.message.content;
                }
                if (typeof resp === 'string') return resp;
                return '';
            };

            const assistantMessage = {
                id: Date.now() + 1,
                role: 'assistant',
                content: normalized(response) || 'I’m ready to help with AMHAS plans, services, enrollment, pricing, or regions. Ask me anything!',
                timestamp: new Date()
            };

            setMessages(prev => [...prev, assistantMessage]);
        } catch (error) {
            console.error('Error sending message:', error);
            const errorMessage = {
                id: Date.now() + 1,
                role: 'assistant',
                content: 'I apologize, but I\'m currently unable to process your request. Please try again later.',
                timestamp: new Date()
            };
            setMessages(prev => [...prev, errorMessage]);
        } finally {
            setIsLoading(false);
        }
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSendMessage();
        }
    };

    const clearChat = () => {
        setMessages([]);
    };

    const getStatusColor = () => {
        switch (trainingStatus) {
            case 'updated': return 'bg-green-500';
            case 'updating': return 'bg-yellow-500';
            case 'outdated': return 'bg-red-500';
            default: return 'bg-gray-500';
        }
    };

    const getStatusText = () => {
        switch (trainingStatus) {
            case 'updated': return 'Updated';
            case 'updating': return 'Updating';
            case 'outdated': return 'Outdated Data';
            default: return 'Unknown';
        }
    };

    return (
        <>
            {/* Floating Chat Button */}
            <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ delay: 2, duration: 0.3 }}
                className="fixed bottom-4 right-4 z-50"
            >
                <button
                    onClick={() => setIsOpen(!isOpen)}
                    className="bg-primary-600 hover:bg-primary-700 text-white rounded-full p-4 shadow-lg hover:shadow-xl transition-all duration-300 group"
                >
                    <MessageCircle size={24} />
                    {/* Notification dot for new messages */}
                    {messages.length > 0 && !isOpen && (
                        <div className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 rounded-full"></div>
                    )}
                </button>
            </motion.div>

            {/* Chat Window */}
            <AnimatePresence>
                {isOpen && (
                    <motion.div
                        initial={{ opacity: 0, scale: 0.8, y: 20 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.8, y: 20 }}
                        transition={{ duration: 0.3 }}
                        className={`fixed bottom-20 right-4 z-50 bg-white rounded-2xl shadow-2xl border border-gray-200 ${isMinimized ? 'w-80 h-16' : 'w-96 h-[500px]'
                            } flex flex-col overflow-hidden`}
                    >
                        {/* Header */}
                        <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-primary-600 text-white">
                            <div className="flex items-center space-x-3">
                                <div className="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
                                    <Bot size={16} />
                                </div>
                                <div>
                                    <h3 className="text-sm font-semibold">AMHAS AI Assistant</h3>
                                    <div className="flex items-center space-x-2">
                                        <div className={`w-2 h-2 rounded-full ${getStatusColor()}`}></div>
                                        <span className="text-xs opacity-90">{getStatusText()}</span>
                                    </div>
                                </div>
                            </div>
                            <div className="flex items-center space-x-2">
                                <button
                                    onClick={() => setIsMinimized(!isMinimized)}
                                    className="p-1 hover:bg-white/20 rounded transition-colors"
                                >
                                    {isMinimized ? <Maximize2 size={16} /> : <Minimize2 size={16} />}
                                </button>
                                <button
                                    onClick={() => setIsOpen(false)}
                                    className="p-1 hover:bg-white/20 rounded transition-colors"
                                >
                                    <X size={16} />
                                </button>
                            </div>
                        </div>

                        {!isMinimized && (
                            <>
                                {/* Messages */}
                                <div className="flex-1 overflow-y-auto p-4 space-y-3">
                                    {messages.length === 0 ? (
                                        <div className="text-center py-8">
                                            <Bot size={32} className="text-gray-300 mx-auto mb-3" />
                                            <h4 className="text-sm font-medium text-gray-900 mb-2">Welcome to AMHAS AI Assistant</h4>
                                            <p className="text-xs text-gray-600 mb-3">
                                                I can help you with questions about healthcare, schools, and AMHAS services.
                                            </p>
                                            <div className="space-y-1 text-xs">
                                                <div className="bg-gray-50 rounded p-2">
                                                    <strong>Healthcare:</strong> Child health, coverage, procedures
                                                </div>
                                                <div className="bg-gray-50 rounded p-2">
                                                    <strong>Schools:</strong> Directory, enrollment, participation
                                                </div>
                                                <div className="bg-gray-50 rounded p-2">
                                                    <strong>Services:</strong> Telehealth, preventive care, claims
                                                </div>
                                            </div>
                                        </div>
                                    ) : (
                                        messages.map((message) => (
                                            <motion.div
                                                key={message.id}
                                                initial={{ opacity: 0, y: 10 }}
                                                animate={{ opacity: 1, y: 0 }}
                                                className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}
                                            >
                                                <div className={`flex items-start space-x-2 max-w-[85%] ${message.role === 'user' ? 'flex-row-reverse space-x-reverse' : ''
                                                    }`}>
                                                    <div className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 ${message.role === 'user'
                                                        ? 'bg-primary-600'
                                                        : 'bg-gray-200'
                                                        }`}>
                                                        {message.role === 'user' ? (
                                                            <User size={12} className="text-white" />
                                                        ) : (
                                                            <Bot size={12} className="text-gray-600" />
                                                        )}
                                                    </div>
                                                    <div className={`rounded-lg px-3 py-2 text-sm ${message.role === 'user'
                                                        ? 'bg-primary-600 text-white'
                                                        : 'bg-gray-100 text-gray-900'
                                                        }`}>
                                                        <div className="whitespace-pre-wrap formatted-content">
                                                            {formatMessage(message.content)}
                                                        </div>
                                                        <p className={`text-xs mt-1 ${message.role === 'user' ? 'text-primary-100' : 'text-gray-500'
                                                            }`}>
                                                            {message.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                                        </p>
                                                    </div>
                                                </div>
                                            </motion.div>
                                        ))
                                    )}

                                    {isLoading && (
                                        <motion.div
                                            initial={{ opacity: 0, y: 10 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            className="flex justify-start"
                                        >
                                            <div className="flex items-start space-x-2 max-w-[85%]">
                                                <div className="w-6 h-6 bg-gray-200 rounded-full flex items-center justify-center flex-shrink-0">
                                                    <Bot size={12} className="text-gray-600" />
                                                </div>
                                                <div className="bg-gray-100 rounded-lg px-3 py-2">
                                                    <div className="flex items-center space-x-2">
                                                        <Loader2 size={12} className="animate-spin text-gray-500" />
                                                        <span className="text-xs text-gray-500">Thinking . . . could take couple minutes please be patient</span>
                                                    </div>
                                                </div>
                                            </div>
                                        </motion.div>
                                    )}

                                    <div ref={messagesEndRef} />
                                </div>

                                {/* Input */}
                                <div className="p-3 border-t border-gray-200">
                                    <div className="flex items-end space-x-2">
                                        <div className="flex-1">
                                            <textarea
                                                ref={inputRef}
                                                value={inputMessage}
                                                onChange={(e) => setInputMessage(e.target.value)}
                                                onKeyPress={handleKeyPress}
                                                placeholder="Ask me anything..."
                                                className="w-full p-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-none"
                                                rows={1}
                                                style={{ minHeight: '36px', maxHeight: '80px' }}
                                            />
                                        </div>
                                        <button
                                            onClick={handleSendMessage}
                                            disabled={!inputMessage.trim() || isLoading}
                                            className="p-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                        >
                                            <Send size={16} />
                                        </button>
                                    </div>
                                    <div className="flex items-center justify-between mt-2">
                                        <p className="text-xs text-gray-500">
                                            Press Enter to send
                                        </p>
                                        {messages.length > 0 && (
                                            <button
                                                onClick={clearChat}
                                                className="text-xs text-gray-500 hover:text-gray-700"
                                            >
                                                Clear chat
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </>
                        )}
                    </motion.div>
                )}
            </AnimatePresence>
        </>
    );
};

export default FloatingAIChat;
